/**
 * Turning what the cashier typed into payments. The server checks every payment again (never more
 * than what is due, cash change computed by the server); this only guides the screen.
 */
import type { PaymentMethod, PaymentRequest } from "../../api/types";
import { parseAmount, type Cents } from "../../lib/money";

export type PlanResult =
  { ok: true; payment: PaymentRequest } | { ok: false; reason: "invalid" | "tooMuch" | "nothingDue" };

/**
 * An empty amount pays the rest. With cash, more than the rest means the customer handed over that
 * much and gets change; other methods can never take more than the rest.
 */
export function planPayment(method: PaymentMethod, typed: string, remaining: Cents): PlanResult {
  if (remaining <= 0) return { ok: false, reason: "nothingDue" };
  const amount = typed.trim() === "" ? remaining : parseAmount(typed);
  if (amount === null || amount <= 0) return { ok: false, reason: "invalid" };
  return pay(method, amount, remaining);
}

/**
 * A banknote button (€ 20) adds the note to the amount the customer hands over, as in the prototype:
 * € 20 + € 10 = 30,00. Nothing is paid yet; Contant (F12) or Enter then pays and shows the change.
 */
export function addNote(typed: string, noteCents: Cents): string {
  const total = (parseAmount(typed) ?? 0) + noteCents;
  return `${String(Math.floor(total / 100))},${String(total % 100).padStart(2, "0")}`;
}

/** What goes back to the customer for the amount typed, when it is more than the rest; otherwise 0. */
export function changeForTyped(typed: string, remaining: Cents): Cents {
  const amount = parseAmount(typed);
  return amount !== null && amount > remaining ? amount - remaining : 0;
}

function pay(method: PaymentMethod, amount: Cents, remaining: Cents): PlanResult {
  if (remaining <= 0) return { ok: false, reason: "nothingDue" };
  if (amount <= remaining) return { ok: true, payment: { method, amountCents: amount } };
  if (method !== "cash") return { ok: false, reason: "tooMuch" };
  return { ok: true, payment: { method, amountCents: remaining, tenderedCents: amount } };
}

export function remainingAfter(due: Cents, payments: readonly PaymentRequest[]): Cents {
  return due - payments.reduce((sum, payment) => sum + payment.amountCents, 0);
}

/** Change to hand back, as the server will compute it: tendered minus amount. */
export function changeFor(payments: readonly PaymentRequest[]): Cents {
  return payments.reduce(
    (sum, payment) => sum + (payment.tenderedCents ?? payment.amountCents) - payment.amountCents,
    0,
  );
}

/** Methods that need the card terminal (or the customer's app) to approve first. */
export function needsConfirmation(method: PaymentMethod): boolean {
  return method !== "cash";
}
